// WolfCard — live state + Wolf-decision UI for the Wolf side game.
//
// Rendered inside StandaloneGamesPanel (expanded view) when the caller
// has a wolf game running on their casual round. Also used inline in the
// Games surfaces for event-scoped games.
//
// State model:
//   Rotation is deterministic from game.config.rotation_order (array of
//   player_ids, length ≥ 3). Wolf on hole H =
//   rotation_order[(H - 1) % rotation_order.length].
//   Per-hole decision is stored server-side in side_game_state via
//   /.netlify/functions/standalone-game { action: 'set_wolf_decision' }.
//
// Scoring per hole (best-ball per team):
//   • Partner mode        — winners +1 each, losers -1 each (2v(N-2))
//   • Lone Wolf           — Wolf ±(N-1), others ∓1  (±3/∓1 at N=4)
//   • Blind Wolf          — 2× Lone: Wolf ±2(N-1), others ∓2  (±6/∓2 at N=4)

import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'

// Route the decision write to the endpoint that owns the game's scope. A
// casual round's Wolf game (standalone_round_id set) goes to standalone-game;
// an event/league/group Wolf game goes to event-admin (which authorizes on
// event ownership). Both expose an identical `set_wolf_decision` action that
// writes the same side_game_state row the read/settlement paths read.
async function callGameAction(game, action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const endpoint = game?.standalone_round_id
    ? '/.netlify/functions/standalone-game'
    : '/.netlify/functions/event-admin'
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

// readOnly: true renders the post-round recap — hides the picker, the
// "Locked / Edit" indicator, and any inline error notes; opens the
// per-hole history by default; drops the current-hole/wolf header in
// favor of a "Final results" title. Used on RoundLeaderboardPage.
export default function WolfCard({ game_id, viewerPlayerId, viewerCurrentHole, autoRefreshMs = 0, readOnly = false }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)
  const [showHistory, setShowHistory] = useState(readOnly)
  // Pending selection before Lock. { mode: 'partner'|'lone'|'blind',
  // partner_player_id?: uuid }
  const [pending, setPending] = useState(null)
  // Editing an already-locked decision on the current hole?
  const [editing, setEditing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await db('get_wolf_state', { game_id })
      setState(data)
    } catch (e) {
      setNote(e.message || 'Could not load Wolf state')
    }
    setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(load, autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  const nameById = useMemo(() => {
    if (!state?.participants) return new Map()
    return new Map(state.participants.map(p => [p.player_id, p.name]))
  }, [state?.participants])

  const setDecision = async (payload) => {
    setBusy(true)
    setNote(null)
    try {
      await callGameAction(state?.game, 'set_wolf_decision', { game_id, ...payload })
      await load()
    } catch (e) {
      setNote(e.message || 'Could not save decision')
    }
    setBusy(false)
  }

  // Current hole tracks the scorecard's active hole when provided by the
  // parent. Falls back to the server's first-undecided-hole when it isn't.
  // Prevents the game from silently jumping ahead every time a decision
  // locks — the scorecard is the source of truth for which hole is now.
  // Derived BEFORE early returns so the useEffect below runs on every
  // render (hooks-order rules — React error #310).
  const currentHole = viewerCurrentHole || state?.current_hole || 1

  // Reset pending/editing when the scorecard advances to a new hole.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPending(null); setEditing(false) }, [currentHole])

  if (loading && !state) {
    return <div style={box}><p style={{ margin: 0, color: 'var(--gray-400)', fontSize: '0.8rem' }}>Loading Wolf…</p></div>
  }
  if (!state) {
    return <div style={box}><p style={{ margin: 0, color: 'var(--red)', fontSize: '0.8rem' }}>{note || 'Wolf state unavailable'}</p></div>
  }
  if (state.error) {
    const msg = state.error === 'wolf_needs_four_players' ? 'Wolf needs at least 3 players.'
              : state.error === 'wolf_needs_three_players' ? 'Wolf needs at least 3 players.'
              : state.error === 'wolf_bad_rotation' ? 'Rotation misconfigured — recreate the game.'
              : state.error === 'need_two_participants' ? 'Wolf needs 4 players on the round.'
              : state.error
    return <div style={box}><p style={{ margin: 0, color: 'var(--gray-400)', fontSize: '0.8rem' }}>{msg}</p></div>
  }

  // state-dependent derivations (safe now — state is non-null past the
  // early returns)
  const rotLen = state.rotation.length
  const currentIdx = currentHole - 1
  const wolfId = state.rotation[currentIdx % rotLen]
  const wolfName = nameById.get(wolfId) || 'Wolf'
  const currentHoleInfo = state.holes.find(h => h.hole === currentHole) || null
  const currentDecision = currentHoleInfo?.mode ? {
    mode: currentHoleInfo.mode,
    partner: currentHoleInfo.partner,
    blind: currentHoleInfo.blind,
  } : null
  const anyScoresOnCurrent = currentHoleInfo && Object.values(currentHoleInfo.scores || {}).some(v => v != null)
  const others = state.rotation.filter(id => id !== wolfId)

  const showPicker = !currentDecision || editing
  const activePick = pending || (currentDecision && !editing ? {
    mode: currentDecision.blind ? 'blind' : currentDecision.mode,
    partner_player_id: currentDecision.partner || null,
  } : null)

  const lockPending = async () => {
    if (!pending) return
    const payload = { hole_number: currentHole, mode: pending.mode }
    if (pending.mode === 'partner') payload.partner_player_id = pending.partner_player_id
    await setDecision(payload)
    setPending(null)
    setEditing(false)
  }

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {readOnly ? '🐺 Wolf' : `Hole ${currentHole}`}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>
            {readOnly ? 'Final results' : <>🐺 {wolfName} is the Wolf</>}
          </div>
        </div>
        {state.unit_value > 0 && (
          <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>${state.unit_value.toFixed(2)}/pt</div>
        )}
      </div>

      {!readOnly && showPicker && (
        <>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
            {wolfName}, tap your choice then Lock it in:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {/* Blind Wolf — first, only when no scores yet on this hole. */}
            {!anyScoresOnCurrent && (
              <button
                onClick={() => setPending({ mode: 'blind' })}
                disabled={busy}
                style={pillStyle(
                  activePick?.mode === 'blind',
                  { borderColor: 'var(--gold)', accent: 'rgba(212, 185, 122, 0.35)' },
                )}
              >
                👁️ Blind (+{2 * (rotLen - 1)}/−2)
              </button>
            )}
            {/* Lone Wolf pill — always available. */}
            <button
              onClick={() => setPending({ mode: 'lone' })}
              disabled={busy}
              style={pillStyle(
                activePick?.mode === 'lone',
                { borderColor: 'var(--gold)' },
              )}
            >
              🐺 Lone (+{rotLen - 1}/−1)
            </button>
            {/* Partner pills — one per non-Wolf player. */}
            {others.map(id => (
              <button
                key={id}
                onClick={() => setPending({ mode: 'partner', partner_player_id: id })}
                disabled={busy}
                style={pillStyle(
                  activePick?.mode === 'partner' && activePick?.partner_player_id === id,
                )}
              >
                🤝 {nameById.get(id) || 'Player'}
              </button>
            ))}
          </div>
          {/* Lock / Cancel bar */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button
              onClick={lockPending}
              disabled={busy || !pending}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 6,
                background: pending ? 'var(--gold)' : 'var(--green-mid)',
                color: pending ? 'var(--green-deep)' : 'var(--gray-400)',
                border: 'none', fontWeight: 700, fontSize: '0.85rem',
                cursor: pending && !busy ? 'pointer' : 'default',
              }}
            >
              🔒 Lock pick
            </button>
            {editing && (
              <button
                onClick={() => { setEditing(false); setPending(null) }}
                disabled={busy}
                style={{
                  padding: '10px 12px', borderRadius: 6,
                  background: 'transparent', color: 'var(--cream)',
                  border: '1px solid var(--green-mid)',
                  fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <p style={{ fontSize: '0.7rem', color: 'var(--gray-400)', margin: 0 }}>
            Best-ball per team. Blind Wolf must be declared before any scores are entered on this hole.
          </p>
        </>
      )}

      {!readOnly && currentDecision && !editing && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--gray-400)' }}>Locked: </span>
            {currentDecision.mode === 'partner'
              ? <>🤝 Partnering with <strong>{nameById.get(currentDecision.partner) || 'Player'}</strong></>
              : currentDecision.blind
                ? <>👁️ Blind Wolf</>
                : <>🐺 Lone Wolf</>}
          </p>
          <button
            onClick={() => setEditing(true)}
            style={{
              padding: '4px 10px', borderRadius: 6,
              background: 'transparent', color: 'var(--gold)',
              border: '1px solid var(--gold)',
              fontSize: '0.75rem', cursor: 'pointer',
            }}
          >
            Edit
          </button>
        </div>
      )}

      {/* Running totals */}
      <div style={{ marginTop: 10, padding: 8, background: 'rgba(0,0,0,0.15)', borderRadius: 6 }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Running totals</div>
        <div style={{ display: 'grid', gap: 4 }}>
          {state.rotation.map(id => {
            const t = state.totals[id] || { points: 0, units: 0 }
            const isPos = t.points > 0
            const isNeg = t.points < 0
            return (
              <div key={id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span>{nameById.get(id) || id.slice(0, 6)}</span>
                <span style={{
                  fontWeight: 600, fontFamily: 'var(--font-mono)',
                  color: isPos ? '#5dd870' : isNeg ? '#e57373' : 'var(--gray-300)',
                }}>
                  {isPos ? '+' : ''}{t.points}
                  {state.unit_value > 0 && (
                    <span style={{ fontWeight: 400, marginLeft: 6, color: 'var(--gray-400)' }}>
                      ({t.units >= 0 ? '+' : ''}${t.units.toFixed(2)})
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {!readOnly && note && (
        <div
          role="alert"
          style={{
            margin: '10px 0 0', padding: '8px 10px', borderRadius: 6,
            background: 'rgba(229, 115, 115, 0.15)',
            border: '1px solid var(--red)',
            color: 'var(--red)', fontSize: '0.8rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span>⚠️ {note}</span>
          <button
            onClick={() => setNote(null)}
            aria-label="Dismiss"
            style={{
              background: 'transparent', border: 'none', color: 'var(--red)',
              cursor: 'pointer', fontSize: '1rem', padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Rotation preview + past-hole history */}
      <button
        onClick={() => setShowHistory(h => !h)}
        style={{
          marginTop: 8, background: 'transparent', border: 'none',
          color: 'var(--gold)', cursor: 'pointer', fontSize: '0.75rem', padding: 0,
        }}
      >
        {showHistory ? 'Hide' : 'Show'} rotation + hole history
      </button>
      {showHistory && (
        <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--gray-300)' }}>
          <div style={{ marginBottom: 6 }}>
            <strong>Rotation:</strong>{' '}
            {state.rotation.map(id => nameById.get(id) || '?').join(' → ')} → repeats
          </div>
          {state.holes.filter(h => h.resolved).map(h => (
            <div key={h.hole} style={{ borderTop: '1px solid var(--green-mid)', padding: '4px 0' }}>
              <strong>H{h.hole}:</strong>{' '}
              {nameById.get(h.wolf)} {h.mode === 'partner' ? `+ ${nameById.get(h.partner)}` : (h.blind ? '(blind)' : '(lone)')}
              {' · '}
              {h.winner === 'halved' ? 'halved' : h.winner === 'wolf' ? 'Wolf side won' : 'Others won'}
              {' · '}
              W:{h.wolf_team_score ?? '—'} vs O:{h.other_team_score ?? '—'}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const box = {
  padding: 10, borderRadius: 8,
  background: 'var(--green-dark)', border: '1px solid var(--green-mid)',
}

function pillStyle(selected, opts = {}) {
  return {
    padding: '6px 12px', borderRadius: 999,
    background: selected ? (opts.accent || 'var(--gold)') : 'transparent',
    color: selected && !opts.accent ? 'var(--green-deep)' : 'var(--cream)',
    border: `1px solid ${selected ? 'var(--gold)' : (opts.borderColor || 'var(--green-mid)')}`,
    fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: selected ? '0 0 0 2px rgba(212, 185, 122, 0.35)' : 'none',
  }
}
